/**
 * scoreTip.ts —— 决策评分提示的存活判定（第 10 轮 R3：事件驱动，取代固定倒计时）
 *
 * 抽成独立模块只有一个目的：把「提示活多久」变成一条可单测的口径，而不是散在渲染函数里的字面量。
 *
 * 时长沿革：
 *   - PM 终验收 PM-NEW-001（第 8 轮）：把线上实测 ≈4000ms 收敛回 PRD 7.4 的 2000ms。
 *   - 第 9 轮 R2：2s 太短、一闪而过，上调到 ≥4000ms（`scoreTipAlive` 时间判定）。
 *   - **第 10 轮 R3（本轮）**：用户直接反馈——弃牌后 AI 连续行动、画面持续变化，任何固定时长
 *     都会「一闪而过」。于是**彻底退役固定倒计时**：`SCORE_TIP_MS` / `scoreTipAlive` 不再作为
 *     自动消失依据（保留为兜底最大时长常量，默认行为已改为事件驱动）。
 *
 *     新口径（R3.1 / R3.4）：一条评分自展示起**常驻可见**，直到发生「下一手操作」事件之一才关闭：
 *       (a) 玩家提交下一个决策动作 / 进入下一步行动阶段需重新行动（由 controller 事件清空 lastScore）；
 *       (b) 玩家显式关闭（✕ / Esc）——由组件层记录 `dismissedAt` 判定。
 *     判定收敛到本模块的纯函数 `scoreTipVisible(lastAt, dismissedAt)`，可单测、无时间依赖。
 */

/**
 * 兜底最大可见时长（ms）——R3 后**不再用于自动消失**，仅保留为「极端情况下的兜底上限」常量，
 * 供需要硬上限的调用方参考。默认行为以事件驱动为准。
 */
export const SCORE_TIP_MS = 4000;

/**
 * 事件驱动的可见性判定（R3）：
 *   - `lastAt` 为 null → 当前无评分，不可见；
 *   - `dismissedAt === lastAt` → 该条评分已被玩家显式关闭（✕ / Esc），不可见；
 *   - 否则常驻可见（是否因「下一手操作」关闭，由 controller 把 lastScore 置空来表达，
 *     那种情况下 `lastAt` 已为 null，天然走第一条）。
 *
 * `at` 用作评分的唯一标识（同一时刻至多一条常驻评分），因此新评分不会被旧的 `dismissedAt` 误判为已关闭。
 */
export function scoreTipVisible(lastAt: number | null, dismissedAt: number | null): boolean {
  if (lastAt === null) return false;
  return lastAt !== dismissedAt;
}
